/**
 * LanguageProvider — synchronises i18next state with the DOM.
 *
 * Responsibilities:
 * - Sets `document.documentElement.dir` to "rtl" when Arabic is active.
 * - Sets `document.documentElement.lang` to the current language code.
 * - Re-renders children when the language changes.
 *
 * Detection priority (configured in @shared/i18n):
 *   URL language prefix > localStorage > browser navigator > default ("en")
 *
 * Supported languages: en, ar, de, es, fr.
 *
 * Must wrap AuthProvider so that auth error messages are translatable.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import i18n, {
  languages,
  type SupportedLanguage,
  supportedLangs,
  changeLanguage as i18nChangeLanguage,
  isRTL,
  normalizeLanguage,
} from "../i18n";

interface LanguageContextValue {
  /** Current active language code. */
  language: SupportedLanguage;
  /** Whether the current language is RTL. */
  rtl: boolean;
  /** All supported languages with metadata. */
  languages: typeof languages;
  /** All supported language codes. */
  supportedLangs: readonly SupportedLanguage[];
  /** Switch the active language. Updates i18next, localStorage, and DOM direction. */
  setLanguage: (lng: SupportedLanguage) => Promise<void>;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(
  undefined,
);

function LanguageSync({ children }: { children: ReactNode }) {
  const { i18n: i18nInstance } = useTranslation();
  const [language, setLanguageState] = useState<SupportedLanguage>(
    () => normalizeLanguage(i18nInstance.language),
  );

  // Keep DOM attributes in sync when i18next language changes.
  useEffect(() => {
    const syncDocumentLanguage = (lng: string) => {
      const lang = normalizeLanguage(lng);
      setLanguageState(lang);

      const dir = languages[lang].dir;
      document.documentElement.dir = dir;
      document.documentElement.lang = lang;
      document.documentElement.style.direction = dir;
    };

    syncDocumentLanguage(i18nInstance.language);
    i18nInstance.on("languageChanged", syncDocumentLanguage);
    return () => {
      i18nInstance.off("languageChanged", syncDocumentLanguage);
    };
  }, [i18nInstance]);

  const setLanguage = useCallback(async (lng: SupportedLanguage) => {
    await i18nChangeLanguage(lng);
  }, []);

  const rtl = isRTL(language);

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      rtl,
      languages,
      supportedLangs,
      setLanguage,
    }),
    [language, rtl, setLanguage],
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  return (
    <I18nextProvider i18n={i18n}>
      <LanguageSync>{children}</LanguageSync>
    </I18nextProvider>
  );
}

/**
 * Access the current language and the setter.
 * Must be used inside `<LanguageProvider>`.
 */
export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx)
    throw new Error("useLanguage must be used within <LanguageProvider>");
  return ctx;
}
